import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import api from "@/lib/api";
import { 
  Activity, ClipboardList, Stethoscope, Heart, MessageSquare, 
  AlertTriangle, Brain, Users, Phone, BarChart3, Shield, FileText,
  CheckCircle2, AlertCircle, Loader2
} from "lucide-react";

// Live status indicator component
const StatusIndicator = ({ status }) => {
  const colors = {
    operational: "bg-green-500",
    degraded: "bg-yellow-500",
    issues: "bg-red-500",
    loading: "bg-slate-500 animate-pulse"
  };
  
  return (
    <div className={`w-2 h-2 rounded-full ${colors[status] || colors.loading}`} title={status} />
  );
};

// Feature Card Component
const FeatureCard = ({ 
  title, 
  icon: Icon, 
  color, 
  features, 
  mainRoute,
  status = "operational"
}) => {
  const navigate = useNavigate();
  
  const colorClasses = {
    emerald: {
      bg: "from-emerald-900/30 to-emerald-800/10",
      border: "border-emerald-600/30",
      icon: "bg-emerald-500/20",
      iconText: "text-emerald-400",
      title: "text-emerald-300",
      hover: "hover:text-emerald-300 hover:bg-emerald-500/10"
    },
    teal: {
      bg: "from-teal-900/30 to-teal-800/10",
      border: "border-teal-600/30",
      icon: "bg-teal-500/20",
      iconText: "text-teal-400",
      title: "text-teal-300",
      hover: "hover:text-teal-300 hover:bg-teal-500/10"
    },
    purple: {
      bg: "from-purple-900/30 to-purple-800/10",
      border: "border-purple-600/30",
      icon: "bg-purple-500/20",
      iconText: "text-purple-400",
      title: "text-purple-300",
      hover: "hover:text-purple-300 hover:bg-purple-500/10"
    },
    pink: {
      bg: "from-pink-900/30 to-pink-800/10",
      border: "border-pink-600/30",
      icon: "bg-pink-500/20",
      iconText: "text-pink-400",
      title: "text-pink-300",
      hover: "hover:text-pink-300 hover:bg-pink-500/10"
    },
    indigo: {
      bg: "from-indigo-900/30 to-indigo-800/10",
      border: "border-indigo-600/30",
      icon: "bg-indigo-500/20",
      iconText: "text-indigo-400",
      title: "text-indigo-300",
      hover: "hover:text-indigo-300 hover:bg-indigo-500/10"
    },
    cyan: {
      bg: "from-cyan-900/30 to-cyan-800/10",
      border: "border-cyan-600/30",
      icon: "bg-cyan-500/20",
      iconText: "text-cyan-400",
      title: "text-cyan-300",
      hover: "hover:text-cyan-300 hover:bg-cyan-500/10"
    },
    red: {
      bg: "from-red-900/30 to-red-800/10",
      border: "border-red-600/30",
      icon: "bg-red-500/20",
      iconText: "text-red-400",
      title: "text-red-300",
      hover: "hover:text-red-300 hover:bg-red-500/10"
    },
    amber: {
      bg: "from-amber-900/30 to-amber-800/10",
      border: "border-amber-600/30",
      icon: "bg-amber-500/20",
      iconText: "text-amber-400",
      title: "text-amber-300",
      hover: "hover:text-amber-300 hover:bg-amber-500/10"
    },
    blue: {
      bg: "from-blue-900/30 to-blue-800/10",
      border: "border-blue-600/30",
      icon: "bg-blue-500/20",
      iconText: "text-blue-400",
      title: "text-blue-300",
      hover: "hover:text-blue-300 hover:bg-blue-500/10"
    }
  };
  
  const c = colorClasses[color] || colorClasses.blue;
  
  return (
    <div className={`p-4 bg-gradient-to-br ${c.bg} border ${c.border} rounded-lg`}>
      <div 
        className="flex items-center justify-between mb-3 cursor-pointer hover:opacity-80"
        onClick={() => navigate(mainRoute)}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 ${c.icon} rounded-lg`}>
            <Icon className={`w-5 h-5 ${c.iconText}`} />
          </div>
          <h3 className={`${c.title} font-semibold`}>{title}</h3>
        </div>
        <StatusIndicator status={status} />
      </div>
      <div className="space-y-1.5">
        {features.map((feature, idx) => (
          <button 
            key={idx}
            onClick={() => navigate(feature.route || mainRoute)} 
            className={`w-full text-left text-xs text-slate-400 ${c.hover} px-2 py-1 rounded transition-colors`}
          >
            • {feature.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Main Operating System Features Component
export default function HospiceOSFeatures({ 
  variant = "full", // "full", "admin", "staff", "patient", "facility"
  showTitle = true
}) {
  const navigate = useNavigate();
  const [systemStatus, setSystemStatus] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkSystemStatus();
    // Check status every 30 seconds
    const interval = setInterval(checkSystemStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkSystemStatus = async () => {
    try {
      // Check health of various services
      const healthChecks = await Promise.allSettled([
        api.get("/health"),
        api.get("/admission-workflow/dashboard").catch(() => ({ data: null })),
        api.get("/escalations/dashboard").catch(() => ({ data: null })),
        api.get("/physician-dashboard/summary").catch(() => ({ data: null })),
        api.get("/rn-dashboard/summary").catch(() => ({ data: null })),
      ]);

      const newStatus = {
        admissions: healthChecks[1].status === "fulfilled" ? "operational" : "degraded",
        referrals: healthChecks[0].status === "fulfilled" ? "operational" : "issues",
        physicianAI: healthChecks[3].status === "fulfilled" ? "operational" : "degraded",
        rnAI: healthChecks[4].status === "fulfilled" ? "operational" : "degraded",
        messaging: "operational",
        dme: "operational",
        escalations: healthChecks[2].status === "fulfilled" ? "operational" : "degraded",
        clinicalAI: "operational"
      };
      
      setSystemStatus(newStatus);
    } catch (error) {
      console.error("Health check error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Define feature sets based on variant
  const allFeatures = {
    admissions: {
      title: "Admissions",
      icon: Activity,
      color: "emerald",
      mainRoute: "/admission-workflow",
      features: [
        { label: "48h RN Visit Tracking", route: "/admission-workflow" },
        { label: "IDG Assessment (5 days)", route: "/admission-workflow" },
        { label: "Benefit Period (90-90-60)", route: "/admission-workflow" },
        { label: "F2F/Recert Windows", route: "/admission-workflow" }
      ]
    },
    referrals: {
      title: "Referral Portal",
      icon: ClipboardList,
      color: "teal",
      mainRoute: "/referral-portal",
      features: [
        { label: "Eligibility Assessment", route: "/referral-portal" },
        { label: "Document Upload & OCR", route: "/referral-portal" },
        { label: "E-Signatures", route: "/referral-portal" },
        { label: "CarePort/AIDIN Integration", route: "/referral-portal" }
      ]
    },
    physicianAI: {
      title: "Physician AI",
      icon: Stethoscope,
      color: "purple",
      mainRoute: "/physician-ai-dashboard",
      features: [
        { label: "CTI Signature Queue", route: "/physician-ai-dashboard" },
        { label: "Order Management", route: "/physician-ai-dashboard" },
        { label: "F2F Encounter Tracking", route: "/physician-ai-dashboard" },
        { label: "AI-Powered Insights", route: "/physician-ai-dashboard" }
      ]
    },
    rnAI: {
      title: "RN AI Dashboard",
      icon: Heart,
      color: "pink",
      mainRoute: "/rn-ai-dashboard",
      features: [
        { label: "Patient Hotlist (Prognosis)", route: "/rn-ai-dashboard" },
        { label: "AI Care Plans", route: "/rn-ai-dashboard" },
        { label: "Handoff Notifications", route: "/rn-ai-dashboard" },
        { label: "IDG/Recert Tracking", route: "/rn-ai-dashboard" }
      ]
    },
    messaging: {
      title: "Physician Messaging",
      icon: MessageSquare,
      color: "indigo",
      mainRoute: "/physician-messaging",
      features: [
        { label: "Secure HIPAA Messaging", route: "/physician-messaging" },
        { label: "Order Clarifications", route: "/physician-messaging" },
        { label: "Clinical Consultations", route: "/physician-messaging" },
        { label: "Message History", route: "/physician-messaging" }
      ]
    },
    dme: {
      title: "DME & Supplies",
      icon: Activity,
      color: "cyan",
      mainRoute: "/dme-ordering",
      features: [
        { label: "Equipment Ordering", route: "/dme-ordering" },
        { label: "Vendor Management", route: "/dme-ordering" },
        { label: "Staff Supply Requests", route: "/staff-supplies" },
        { label: "Delivery Tracking", route: "/dme-ordering" }
      ]
    },
    escalations: {
      title: "Escalations",
      icon: AlertTriangle,
      color: "red",
      mainRoute: "/escalations",
      features: [
        { label: "4-Level Auto Escalation", route: "/escalations" },
        { label: "Supervisor Notifications", route: "/escalations" },
        { label: "Backup Assignments", route: "/escalations" },
        { label: "Compliance Audit Trail", route: "/escalations" }
      ]
    },
    clinicalAI: {
      title: "Clinical AI",
      icon: Brain,
      color: "amber",
      mainRoute: "/clinical-ai",
      features: [
        { label: "Goals of Care Chatbot", route: "/clinical-ai" },
        { label: "Symptom Prediction", route: "/clinical-ai" },
        { label: "SBAR Documentation", route: "/sbar-tool" },
        { label: "Voice-to-Text", route: "/hospice-ehr/voice-documentation" }
      ]
    },
    hope: {
      title: "HOPE Voice Pipeline",
      icon: Activity,
      color: "teal",
      mainRoute: "/hope-dashboard",
      features: [
        { label: "Voice-to-SOT Recording", route: "/hope-dashboard" },
        { label: "Manual SOT Entry", route: "/hope-dashboard" },
        { label: "Symptom Trends", route: "/hope-dashboard" },
        { label: "CMS iQIES Export", route: "/hope-dashboard" }
      ]
    },
    familyPortal: {
      title: "Family Symptom Portal",
      icon: Heart,
      color: "pink",
      mainRoute: "/family-symptoms",
      features: [
        { label: "Symptom Overview", route: "/family-symptoms" },
        { label: "Trend Charts", route: "/family-symptoms" },
        { label: "Care Team Contacts", route: "/family-symptoms" },
        { label: "Educational Resources", route: "/family-symptoms" }
      ]
    },
    idgMeeting: {
      title: "IDG Live Meeting",
      icon: Users,
      color: "indigo",
      mainRoute: "/idg-meeting",
      features: [
        { label: "Start Meeting", route: "/idg-meeting" },
        { label: "Patient Queue", route: "/idg-meeting" },
        { label: "AI Recommendations", route: "/idg-meeting" },
        { label: "Action Items", route: "/idg-meeting" }
      ]
    },
    ehr: {
      title: "Hospice EHR",
      icon: FileText,
      color: "blue",
      mainRoute: "/hospice-ehr",
      features: [
        { label: "Patient Records", route: "/hospice-ehr" },
        { label: "Clinical Documentation", route: "/hospice-ehr/voice-documentation" },
        { label: "Medication Orders", route: "/hospice-ehr/medications" },
        { label: "Compliance Management", route: "/hospice-ehr/compliance" }
      ]
    }
  };

  // Define which features to show based on variant
  const variantFeatures = {
    full: ["admissions", "referrals", "physicianAI", "rnAI", "messaging", "dme", "escalations", "clinicalAI", "hope", "familyPortal", "idgMeeting"],
    admin: ["admissions", "referrals", "physicianAI", "rnAI", "messaging", "dme", "escalations", "clinicalAI", "hope", "familyPortal", "idgMeeting", "ehr"],
    staff: ["admissions", "rnAI", "messaging", "dme", "clinicalAI", "hope", "idgMeeting", "ehr"],
    patient: ["messaging", "clinicalAI", "familyPortal"],
    facility: ["referrals", "messaging"]
  };

  const featuresToShow = variantFeatures[variant] || variantFeatures.full;

  // Secondary actions based on variant
  const secondaryActions = {
    full: [
      { label: "Staff Portal", icon: Users, route: "/staff-portal" },
      { label: "Telehealth", icon: Phone, route: "/telehealth" },
      { label: "Call Center", icon: MessageSquare, route: "/admin/triage" },
      { label: "Analytics", icon: BarChart3, route: "/admin/video-analytics" },
      { label: "HIPAA", icon: Shield, route: "/admin/hipaa" },
      { label: "Training", icon: FileText, route: "/academy" }
    ],
    admin: [
      { label: "Staff Portal", icon: Users, route: "/staff-portal" },
      { label: "Telehealth", icon: Phone, route: "/telehealth" },
      { label: "Analytics", icon: BarChart3, route: "/admin/video-analytics" },
      { label: "HIPAA", icon: Shield, route: "/admin/hipaa" },
      { label: "Training", icon: FileText, route: "/academy" }
    ],
    staff: [
      { label: "My Caseload", icon: Users, route: "/staff-portal" },
      { label: "Telehealth", icon: Phone, route: "/telehealth" },
      { label: "Training", icon: FileText, route: "/academy" }
    ],
    patient: [
      { label: "My Care Team", icon: Users, route: "/patient/care-team" },
      { label: "Telehealth", icon: Phone, route: "/telehealth" }
    ],
    facility: [
      { label: "My Referrals", icon: ClipboardList, route: "/facility/referrals" },
      { label: "Contact Us", icon: Phone, route: "/contact" }
    ]
  };

  const actionsToShow = secondaryActions[variant] || secondaryActions.full;

  // Calculate overall system health
  const healthCounts = Object.values(systemStatus);
  const operationalCount = healthCounts.filter(s => s === "operational").length;
  const overallHealth = healthCounts.length === 0 ? "loading" : 
    operationalCount === healthCounts.length ? "operational" :
    operationalCount > healthCounts.length / 2 ? "degraded" : "issues";

  return (
    <Card className="bg-slate-800/50 border-slate-700">
      {showTitle && (
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Hospice Operating System</CardTitle>
            <div className="flex items-center gap-2 text-sm">
              <StatusIndicator status={overallHealth} />
              <span className={`text-xs ${
                overallHealth === "operational" ? "text-green-400" :
                overallHealth === "degraded" ? "text-yellow-400" :
                overallHealth === "issues" ? "text-red-400" : "text-slate-400"
              }`}>
                {loading ? "Checking..." : 
                 overallHealth === "operational" ? "All Systems Operational" :
                 overallHealth === "degraded" ? "Some Services Degraded" : "Issues Detected"}
              </span>
            </div>
          </div>
        </CardHeader>
      )}
      <CardContent>
        {/* Primary Feature Cards */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${
          featuresToShow.length > 4 ? "lg:grid-cols-3 xl:grid-cols-4" : "lg:grid-cols-2"
        } gap-4 mb-6`}>
          {featuresToShow.map((featureKey) => {
            const feature = allFeatures[featureKey];
            if (!feature) return null;
            return (
              <FeatureCard
                key={featureKey}
                {...feature}
                status={systemStatus[featureKey] || "loading"}
              />
            );
          })}
        </div>

        {/* Secondary Quick Actions */}
        {actionsToShow.length > 0 && (
          <div className={`grid grid-cols-${Math.min(actionsToShow.length, 6)} gap-2`}>
            {actionsToShow.map((action, idx) => (
              <Button
                key={idx}
                variant="outline"
                className="h-14 flex-col border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white text-xs"
                onClick={() => navigate(action.route)}
              >
                <action.icon className="w-4 h-4 mb-1" />
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Export individual components for flexibility
export { FeatureCard, StatusIndicator };
